// The contracted limits of the installation: how much space all the files may
// take together, and how many administrators and contacts it may have. The API
// refuses what goes past them with a 403 carrying one of these codes (see
// App\Lib\UsageLimits in the backend), and this module turns that into the
// warning the user sees, worded like the one CleverTime shows.
//
// Nothing here runs on the happy path: a call that is not refused for a limit
// is left to the handling it already had.

import { readErrorBody } from "./fileErrors";

export const STORAGE_LIMIT = "STORAGE_LIMIT_REACHED";
export const USER_LIMIT = "USER_LIMIT_REACHED";

const CONTACT_EMAIL = "contacto@cleverbsolutions.com";
const GIB = 1073741824;

// Parallel uploads are refused one by one, and each would open its own dialog.
// One is enough to say it.
let dialogOpen = false;

function escapeHtml (text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const MIB = 1048576;
const KIB = 1024;

// The largest unit the figure reaches, so neither an installation that has just
// started nor the little space left near the limit reads "0 GB". The decimals are
// fixed, so two sizes a few MB apart never print the same ("1,120 GB" against
// "1,123 GB"), and dropped only for a whole figure such as the limit ("10 GB").
// Truncated, not rounded: it never shows more space in use than there is.
export function formatStorage (bytes) {
  let unit = "KB";
  let size = KIB;
  let decimals = 0;

  if (bytes >= GIB) {
    unit = "GB";
    size = GIB;
    decimals = 3;
  } else if (bytes >= MIB) {
    unit = "MB";
    size = MIB;
    decimals = 2;
  }

  const factor = Math.pow(10, decimals);
  const value = Math.floor(bytes / size * factor) / factor;
  const digits = Number.isInteger(value) ? 0 : decimals;

  return value.toLocaleString("es-ES", { minimumFractionDigits: digits, maximumFractionDigits: digits }) + " " + unit;
}

// The body of the refusal when the error is one of these limits, null otherwise.
export async function limitErrorBody (error) {
  if (!error || !error.response || error.response.status !== 403) return null;

  const body = await readErrorBody(error);

  return body && (body.code === STORAGE_LIMIT || body.code === USER_LIMIT) ? body : null;
}

export function showLimitDialog (vm, body) {
  if (dialogOpen) return Promise.resolve();

  let title;
  let text;

  if (body.code === STORAGE_LIMIT) {
    title = vm.$t("storage_limit_title");
    // The API sends the figures to administrators only; a contact gets the
    // same warning without them.
    text = body.limit_size
      ? vm.$t("storage_limit_body", { limit: formatStorage(body.limit_size) })
      : vm.$t("storage_limit_body_no_size");
  } else {
    title = vm.$t("user_limit_title");
    text = vm.$t(body.role === "admin" ? "user_limit_body_admin" : "user_limit_body_contact", { limit: body.limit });
  }

  const html = "<p>" + escapeHtml(text) + "</p>"
    + "<p>" + escapeHtml(vm.$t("limit_contact")) + " "
    + '<a href="mailto:' + CONTACT_EMAIL + '">' + CONTACT_EMAIL + "</a></p>";

  dialogOpen = true;

  // vuejs-dialog resolves on OK. Closed any other way it rejects, and that has
  // to free the guard too instead of leaving an unhandled rejection behind.
  const closed = () => {
    dialogOpen = false;
  };

  return Promise.resolve(vm.$dialog.alert({ title: title, body: html }, {
    html: true,
    customClass: "cd-dialog cd-dialog--warning cd-dialog--wide"
  })).then(closed, closed);
}

// For the .catch of a call that can be refused for a limit: shows the warning and
// answers true when it was one, false when the error is somebody else's to handle.
// The dialog is not waited for, so the caller can tidy up straight away.
export async function handleLimitError (vm, error) {
  const body = await limitErrorBody(error);

  if (!body) return false;

  showLimitDialog(vm, body);

  return true;
}
